import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin, Button, Empty, Alert } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import axiosInstance from '../../variables/api/axiosInstance';
import logoCarteBrune from '../../assets/images/logo.png';
import { IndicesReedition, markPrinted, wasPrinted } from './indiceReedition';
import { useReglagesIndices } from './useReglagesIndices';
import './attestations.css';
import BlocSignature from '../../components/BlocSignature';
import { useAvertissementSignature } from '../../components/avertissementSignature';
import CaseApposerSignature from '../../components/CaseApposerSignature';
import { tDocument, useLangueDocument } from '../../i18n/documents';

const CODE_DOCUMENT = 'CONFIRMATION_GARANTIE';

/* ─────────────────────────────────────────────────────────────
   CONFIRMATION DE GARANTIE — dossier TE, à destination du bureau homologue.

   Sur un dossier ET (accident au Togo, véhicule étranger), le bureau DEMANDE la
   confirmation à son homologue et la reçoit : rien à produire ici. Sur un dossier
   TE, le sens est inversé — l'homologue interroge le Togo, la compagnie togolaise
   répond au bureau, et le bureau lui adresse cette lettre, signée du Secrétaire
   Exécutif. C'est ce document-là qui part littéralement à l'homologue.

   Route : /sinistres/:id/confirmation-garantie
   ───────────────────────────────────────────────────────────── */
export default function ConfirmationGarantie() {
  const { confirmerPuisImprimer } = useAvertissementSignature(CODE_DOCUMENT);
  const [apposer, setApposer] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  // Deux langues sur cette page : celle de l'agent pour l'écran (boutons, alertes),
  // celle du bureau homologue pour la lettre, qui lui est adressée.
  const { t } = useTranslation('documents');
  const reglagesIndices = useReglagesIndices();
  const [sinistre, setSinistre] = useState(null);
  const [assure, setAssure] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sRes = await axiosInstance.get(`/v1/sinistres/${id}`);
      const s = sRes.data?.data;
      setSinistre(s);
      if (s?.assureTrackingId) {
        try {
          const aRes = await axiosInstance.get(`/v1/assures/${s.assureTrackingId}`);
          setAssure(aRes.data?.data);
        } catch { /* la lettre s'imprime sans, les champs restent à compléter */ }
      }
    } catch {
      setSinistre(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const { langue, pret } = useLangueDocument(sinistre?.organismeHomologueTrackingId);
  const tDoc = tDocument(langue);

  if (loading) return <div className="att-page"><Spin /></div>;
  if (!sinistre) {
    return (
      <div className="att-page">
        <Empty description={t('ecran.sinistreIntrouvable')} />
        <Button onClick={() => navigate('/sinistres')} icon={<ArrowLeftOutlined />} style={{ marginTop: 12 }}>
          {t('ecran.retour')}
        </Button>
      </div>
    );
  }

  const fmtDateShort = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '-');
  const vide = <span className="cg-a-completer">{tDoc('commun.aCompleter')}</span>;
  const ou = (valeur) => (valeur ? String(valeur).toUpperCase() : vide);

  /* Destinataire : le bureau homologue, celui qui a posé la question. Sans lui, on
     laisse la ligne à compléter à la main plutôt que d'inventer un nom. */
  const bureauDestinataire = sinistre.organismeHomologueRaisonSociale
    || (sinistre.paysGestionnaireLibelle
      ? tDoc('confirmationGarantie.bureauNationalDe', { pays: sinistre.paysGestionnaireLibelle }) : null);

  const dateFaite = sinistre.dateConfirmationGarantie || dayjs().format('YYYY-MM-DD');
  const dejaImprime = wasPrinted('confirmation-garantie', id);
  const estTe = sinistre.typeSinistre === 'SURVENU_ETRANGER';
  const garantieAcquise = sinistre.garantieAcquise === true;

  const lignes = [
    [tDoc('confirmationGarantie.lignes.notreSinistre'), ou(sinistre.numeroSinistreLocal)],
    [tDoc('confirmationGarantie.lignes.votreSinistre'), ou(sinistre.numeroSinistreHomologue)],
    [tDoc('confirmationGarantie.lignes.dateAccident'), fmtDateShort(sinistre.dateAccident)],
    [tDoc('confirmationGarantie.lignes.immatriculation'), ou(assure?.immatriculation || sinistre.assureImmatriculation)],
    [tDoc('confirmationGarantie.lignes.assure'), ou(assure?.nomComplet || sinistre.assureNomComplet)],
    [tDoc('confirmationGarantie.lignes.carteBrune'), ou(assure?.numeroAttestation)],
    [tDoc('confirmationGarantie.lignes.police'), ou(assure?.numeroPolice || sinistre.numeroPoliceAssureur)],
    [tDoc('confirmationGarantie.lignes.assureur'), ou(sinistre.organismeMembreRaisonSociale)],
  ];

  return (
    <div className="att-page">
      <div className="att-toolbar no-print">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/sinistres/${id}`)}>
          {t('ecran.retourDossier')}
        </Button>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <CaseApposerSignature codeDocument={CODE_DOCUMENT} valeur={apposer} onChange={setApposer} />
          {/* Pas d'impression avant de connaître la langue du destinataire : la lettre
              partirait en français à un bureau anglophone. */}
          <Button type="primary" icon={<PrinterOutlined />} disabled={!pret}
            onClick={() => confirmerPuisImprimer(() => { markPrinted('confirmation-garantie', id); window.print(); })}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
            {t('ecran.imprimer')}
          </Button>
        </div>
      </div>

      {/* Deux avertissements à l'écran seulement : la lettre, elle, s'imprime telle
          quelle — l'agent complétera à la main ou corrigera le dossier. */}
      {!estTe && (
        <Alert type="warning" showIcon className="no-print" style={{ marginBottom: 12, borderRadius: 8 }}
          message={t('ecran.confirmationGarantie.alerteEtTitre')}
          description={t('ecran.confirmationGarantie.alerteEtTexte')} />
      )}
      {!garantieAcquise && (
        <Alert type="warning" showIcon className="no-print" style={{ marginBottom: 12, borderRadius: 8 }}
          message={t('ecran.confirmationGarantie.alerteGarantieTitre')}
          description={t('ecran.confirmationGarantie.alerteGarantieTexte')} />
      )}
      {langue === 'en' && (
        <Alert type="info" showIcon className="no-print" style={{ marginBottom: 12, borderRadius: 8 }}
          message={t('ecran.lettreEnAnglais')} />
      )}

      <div className="att-sheet">
        <div style={{ position: 'absolute', top: '8mm', right: '12mm', zIndex: 2 }}>
          <IndicesReedition
            dejaModifie={!!sinistre?.dejaModifie}
            dejaImprime={dejaImprime}
            reglages={reglagesIndices}
          />
        </div>

        {/* En-tête : le même que l'attestation de déclaration. */}
        <div className="att-header">
          <div className="att-header-left">
            BUREAU NATIONAL TOGOLAIS<br />
            SYSTÈME CARTE BRUNE CEDEAO
          </div>
          <img src={logoCarteBrune} alt="Carte Brune CEDEAO" className="att-logo-img" />
          <div className="att-header-right">
            TOGO NATIONAL BUREAU<br />
            ECOWAS BROWN CARD
          </div>
        </div>

        {/* Destinataire et date, alignés à droite comme sur le courrier d'origine. */}
        <div className="cg-destinataire">
          <div>{bureauDestinataire ? bureauDestinataire.toUpperCase() : <>{tDoc('confirmationGarantie.bureauNational')} {vide}</>}</div>
          <div className="cg-lieu-date">{tDoc('confirmationGarantie.lomeLe', { date: fmtDateShort(dateFaite) })}</div>
        </div>

        <div style={{ textAlign: 'center', margin: '10mm 0 8mm' }}>
          <h1 className="att-title" style={{ textDecoration: 'underline', fontSize: '15pt' }}>
            {tDoc('confirmationGarantie.titre')}
          </h1>
        </div>

        <div className="cg-corps">
          <Trans t={tDoc} i18nKey="confirmationGarantie.corpsConfirmation" components={{ strong: <strong /> }} />
        </div>

        <table className="att-info-table cg-table">
          <tbody>
            {lignes.map(([libelle, valeur]) => (
              <tr key={libelle}>
                <td className="att-label cg-label">{libelle}{tDoc('commun.deuxPoints')}</td>
                <td className="att-value">{valeur}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="cg-corps">
          {tDoc('confirmationGarantie.corpsRestitution')}
        </div>

        <div className="cg-salutation">{tDoc('confirmationGarantie.salutation')}</div>

        <div className="att-pour-bncb">{tDoc('commun.secretaireExecutif')}</div>
        <div className="att-signature-espace">
          <BlocSignature
            codeDocument={CODE_DOCUMENT}
            actif={apposer}
            sinistreTrackingId={sinistre.sinistreTrackingId}
          />
        </div>

        <div className="att-footer">
          {tDoc('commun.piedAdresse')}<br />
          {tDoc('commun.piedCourriel')}
        </div>
      </div>
    </div>
  );
}
